import { SCAN_FORMATS } from './symbologies';

/**
 * Getting a barcode detector, honestly.
 *
 * PRESENCE IS NOT SUPPORT. `'BarcodeDetector' in window` is TRUE on Chrome
 * and Edge desktop on Windows and Linux, where there is no platform backend
 * at all and every detect() rejects. A truthiness check on the constructor
 * therefore ships a scan button that opens a camera and never decodes
 * anything. The only sound probe is to await
 * `BarcodeDetector.getSupportedFormats()` and look at what actually comes
 * back — and then to check that the formats we need are IN that list, since
 * a backend may support a subset.
 *
 * Safari (every version) and Firefox do not implement it at all, so the WASM
 * fallback is the primary path for a large share of readers rather than an
 * edge case, and it has to be good.
 *
 * THE FALLBACK PHONES HOME UNLESS TOLD NOT TO. `barcode-detector`'s shipped
 * bundle hardcodes a jsDelivr URL for its WASM and fetches it at SCAN TIME.
 * setZXingModuleOverrides points it at our own copy, placed in public/ by
 * scripts/copy-zxing-wasm.mjs. That has to happen before the first detect,
 * which is why it is here and not somewhere a caller might forget.
 */

/** Where our own copy of the reader WASM lives. */
export const wasmUrl = () => {
	const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
	return `${base}/zxing_reader.wasm`;
};

let nativeProbe = null;

/**
 * The formats the platform's own BarcodeDetector supports, or [] when there
 * is no usable implementation. Probed once; the answer cannot change inside
 * a session.
 */
export async function nativeFormats() {
	if (!nativeProbe) {
		nativeProbe = (async () => {
			const Ctor = typeof window !== 'undefined' ? window.BarcodeDetector : undefined;
			if (!Ctor || typeof Ctor.getSupportedFormats !== 'function') return [];
			try {
				const formats = await Ctor.getSupportedFormats();
				return Array.isArray(formats) ? formats : [];
			} catch (e) {
				// A throwing probe is a browser telling us there is no backend.
				return [];
			}
		})();
	}
	return nativeProbe;
}

export function resetDetectorProbe() {
	nativeProbe = null;
}

const wanted = formats => {
	const list = Array.isArray(formats) && formats.length ? formats : SCAN_FORMATS;
	return list.filter(format => SCAN_FORMATS.includes(format));
};

/**
 * getDetector(formats) -> { detect(source) -> [{ rawValue, format }], kind }
 *
 * `kind` is 'native' or 'wasm', which the modal shows: a reader who knows
 * the fallback is running knows why the first scan took a moment.
 */
export async function getDetector(formats) {
	const want = wanted(formats);
	const native = await nativeFormats();
	// Every format we were asked for has to be there. A backend that lists
	// only qr_code is not a detector for a field that expects Code 128 — and
	// silently decoding fewer formats than the author configured is exactly
	// the kind of "it works on my phone" this probe exists to end.
	const covered = want.length > 0 && want.every(format => native.includes(format));
	if (covered) {
		const detector = new window.BarcodeDetector({ formats: want });
		return {
			kind: 'native',
			formats: want,
			detect: source => detector.detect(source),
		};
	}

	const mod = await import('barcode-detector/ponyfill');
	// Before the first detect, and idempotent — the ponyfill memoises the
	// module it builds from these overrides.
	mod.setZXingModuleOverrides({
		locateFile: (path, prefix) => (path.endsWith('.wasm') ? wasmUrl() : `${prefix}${path}`),
	});
	const detector = new mod.BarcodeDetector({ formats: want });
	return {
		kind: 'wasm',
		formats: want,
		detect: source => detector.detect(source),
	};
}

/**
 * Whether a scan can be offered at all. A button that opens a camera the
 * browser will not give us is worse than no button, so ScanButton renders
 * nothing when this is false.
 *
 * getUserMedia needs a SECURE CONTEXT: https in production, localhost in
 * dev. A LAN address like the dev proxy's is NOT a secure context and the
 * camera simply will not appear there — which is a confusing five minutes
 * unless something says so, hence the `reason`.
 */
export function scanAvailability() {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return { available: false, reason: 'no-window' };
	}
	if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
		return {
			available: false,
			reason: window.isSecureContext === false ? 'insecure-context' : 'no-camera-api',
		};
	}
	return { available: true, reason: null };
}
